








































import Component from 'vue-class-component'
import BasicComponent from "@/global/BasicComponent";
import {Prop, Watch} from "vue-property-decorator";
import {ConfiguredImageDto, ImageDto, ImageFilterDto} from "@swagger";
import ImagePreview from "@/components/ImagePreview.vue";
import SelectableImagePreview from "@/components/SelectableImagePreview.vue";
import HelpTooltip from "@/components/HelpTooltip.vue";
import VirtualScroll from "@/components/VirtualScroll.vue";

@Component({
  components: {VirtualScroll, HelpTooltip, SelectableImagePreview, ImagePreview}
})
export default class ImageSelector extends BasicComponent {

  @Prop([Array, Object]) value: ConfiguredImageDto | ConfiguredImageDto[]; // The selected imageSelection
  @Prop(Array) images: ImageDto[];
  @Prop(Array) recentlyUsedImages: ImageDto[];
  @Prop(Number) productId: number;
  @Prop(String) filter: ImageFilterDto;
  /** Optional, if the image is a detailed configuration for a given sub-product */
  @Prop(Boolean) multiple: boolean;
  /** Automatically selects all imageSelection; read-only */
  @Prop(Boolean) selectAll: boolean;
  @Prop(Boolean) dense: boolean; // When true, the imageSelection are shown only once, without an extra section for favorite and recent imageSelection
  /**
   * The number of parent references to go up in the DOM tree until the scrollable container
   */
  @Prop(Number) scrollableContainerParentLayers: number;

  isMounted: boolean = false;

  created() {
    if (this.selectAll) {
      this.selectAllImages();
    }
  }

  mounted() {
    this.isMounted = true;
  }

  get selectHintText() {
    if (this.isMultiple) return this.$t("CONFIGURATOR_HINT_CHOOSE_MULTIPLE");
    else return this.$t("CONFIGURATOR_HINT_CHOOSE_ONE");
  }

  get favoriteImages(): ImageDto[] {
    return this.images.filter(image => image.favorite);
  }

  get hasFavoriteImages(): boolean {
    return !this.dense && !this.isReadonly && this.favoriteImages.length > 0;
  }

  get hasRecentlyUsedImages(): boolean {
    return !this.dense && !this.isReadonly && this.recentlyUsedImages && this.recentlyUsedImages.length > 0;
  }

  get isReadonly(): boolean {
    return this.selectAll;
  }

  get isMultiple(): boolean {
    return this.multiple || this.selectAll;
  }

  selectAllImages(): void {
    this.$emit("input", this.images.map(image => this.imageToConfiguredImage(image)));
  }

  isSelected(image: ImageDto): boolean {
    if (this.selectAll) return true;
    if (!this.isMultiple) {
      if (!this.value) return false;
      return (this.value as ConfiguredImageDto).uniqueImageId === image.uniqueId;
    }
    for (let i of (this.value as ConfiguredImageDto[])) {
      if (i.uniqueImageId === image.uniqueId) return true;
    }
    return false;
  }

  onSelect(image: ImageDto): void {
    if (this.isReadonly) return;
    if (this.isSelected(image)) {
      if (this.isMultiple) {
        this.deselect(image);
      }
    } else {
      if (this.isMultiple) {
        (this.value as ConfiguredImageDto[]).push(this.imageToConfiguredImage(image));
      } else {
        this.$emit("input", this.imageToConfiguredImage(image));
      }
    }
  }

  imageToConfiguredImage(image: ImageDto): ConfiguredImageDto {
    return {
      uniqueImageId: image.uniqueId,
      imageId: image.id,
      imageCropId: image.imageCropId,
      productId: this.productId,
      filter: this.filter
    };
  }

  getContainer(): Element {
    if (! this.isMounted) return null;
    const parents = this.scrollableContainerParentLayers ?? 1;
    let el = this.$el;
    for (let i = 0; i<parents; i++) {
      el = el.parentElement;
    }
    return el;
  }

  onContainerSizeChange(): void {
    const allImagesScroller = this.$refs.allImages as VirtualScroll;
    console.log("Container size changed");
    if (! allImagesScroller) return;
    allImagesScroller.displayAdditionalItemsIfNecessary();
  }

  @Watch("multiple")
  onMultipleChange(multiple: boolean, oldMultiple: boolean) {
    return;
    if (multiple) {
      this.$emit("input", this.value ? [this.value] : []);
    } else {
      this.$emit("input", this.value[0]);
    }
  }

  @Watch("images")
  onImagesChange(images: ImageDto[], oldImages: ImageDto[]) {
    if (this.selectAll) {
      if (!oldImages || !this.imageArraysEqual(images, oldImages)) {
        this.selectAllImages();
      }
    }
  }

  @Watch("selectAll")
  onSelectAllChange() {
    if (this.selectAll) {
      this.selectAllImages();
    }
  }

  private imageArraysEqual(a: ImageDto[], b: ImageDto[]): boolean {
    if (a.length !== b.length) return false;
    const set = new Set(b.map(image => image.uniqueId));
    for (let image of a) {
      if (!set.has(image.uniqueId)) return false;
    }
    return true;
  }

  private deselect(image: ImageDto): void {
    const images = this.value as ConfiguredImageDto[];
    for (let i = 0; i < images.length; i++) {
      if (images[i].uniqueImageId === image.uniqueId) {
        images.splice(i, 1);
        return;
      }
    }
  }

}

